<template>
  <div class="reference-list-container">
    <!-- Header -->
    <div class="list-header">
      <h4 class="list-title">
        <svg class="title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
        </svg>
        Reference Documents
        <span v-if="documents.length > 0" class="document-count">({{ documents.length }})</span>
      </h4>
      <button 
        v-if="documents.length > 1" 
        @click="toggleReorderMode"
        class="reorder-button"
        :class="{ 'active': isReorderMode }"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
        </svg>
        {{ isReorderMode ? 'Done' : 'Reorder' }}
      </button>
    </div>

    <!-- Empty State -->
    <div v-if="documents.length === 0" class="empty-state">
      <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
      </svg>
      <p class="empty-text">No reference documents uploaded yet</p>
      <p class="empty-subtext">Upload documents using the area above</p>
    </div>

    <!-- Document List -->
    <div v-else class="document-list">
      <div 
        v-for="(document, index) in documents" 
        :key="document.id"
        class="document-item"
        :class="{ 'reorder-mode': isReorderMode, 'deleting': deletingDocId === document.id }"
      >
        <!-- Reorder Handle -->
        <div v-if="isReorderMode" class="reorder-handle">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2M20 8V6a2 2 0 00-2-2h-2M20 16v2a2 2 0 01-2 2h-2"></path>
          </svg>
        </div>

        <!-- File Icon -->
        <div class="file-icon">
          <svg v-if="getFileType(document) === 'pdf'" class="icon-pdf" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <svg v-else-if="getFileType(document) === 'image'" class="icon-image" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <svg v-else-if="getFileType(document) === 'document'" class="icon-document" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <svg v-else class="icon-file" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        </div>

        <!-- File Info -->
        <div class="file-info">
          <div class="file-name">{{ document.original_filename }}</div>
          <div class="file-meta">
            <span class="file-size">{{ formatFileSize(document.file_size) }}</span>
            <span class="file-separator">•</span>
            <span class="file-type">{{ getFileTypeLabel(document) }}</span>
            <span v-if="document.uploaded_at" class="file-separator">•</span>
            <span v-if="document.uploaded_at" class="file-date">{{ formatDate(document.uploaded_at) }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="file-actions">
          <!-- Download Button -->
          <button 
            @click="downloadDocument(document)"
            class="action-button download-button"
            title="Download"
            :disabled="downloadingDocId === document.id"
          >
            <svg v-if="downloadingDocId !== document.id" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>

          <!-- Delete Button -->
          <button 
            @click="confirmDeleteDocument(document)"
            class="action-button delete-button"
            title="Delete"
            :disabled="deletingDocId === document.id"
          >
            <svg v-if="deletingDocId !== document.id" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Delete Document</h3>
          <button @click="cancelDelete" class="modal-close">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="modal-icon">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <p class="modal-message">
            Are you sure you want to delete <strong>{{ documentToDelete?.original_filename }}</strong>?
          </p>
          <p class="modal-warning">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button @click="cancelDelete" class="btn-cancel">Cancel</button>
          <button @click="deleteDocument" class="btn-danger" :disabled="deletingDocId">
            <span v-if="deletingDocId">Deleting...</span>
            <span v-else>Delete Document</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReferenceDocumentList',
  props: {
    documents: {
      type: Array,
      default: () => []
    },
    entryId: {
      type: [String, Number],
      required: true
    },
    entryType: {
      type: String,
      required: true,
      validator: (value) => ['education', 'experience', 'activity'].includes(value)
    }
  },
  emits: ['document-deleted', 'documents-reordered'],
  data() {
    return {
      isReorderMode: false,
      showDeleteModal: false,
      documentToDelete: null,
      deletingDocId: null,
      downloadingDocId: null
    }
  },
  methods: {
    toggleReorderMode() {
      this.isReorderMode = !this.isReorderMode
      // TODO: Implement drag & drop reordering
    },

    getFileType(document) {
      const extension = document.original_filename?.split('.').pop()?.toLowerCase()
      if (!extension) return 'file'
      
      if (extension === 'pdf') return 'pdf'
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image'
      if (['doc', 'docx'].includes(extension)) return 'document'
      return 'file'
    },

    getFileTypeLabel(document) {
      const type = this.getFileType(document)
      switch (type) {
        case 'pdf': return 'PDF'
        case 'image': return 'Image'
        case 'document': return 'Document'
        default: return 'File'
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      })
    },

    getCvId() {
      // Get CV ID from the window configuration set by Rails
      if (typeof window !== 'undefined' && window.cvCreatorConfig && window.cvCreatorConfig.cvId) {
        return window.cvCreatorConfig.cvId
      }
      return null
    },

    async downloadDocument(doc) {
      if (this.downloadingDocId) return
      
      const cvId = this.getCvId()
      if (!cvId) {
        console.error('Cannot download document: No CV ID available')
        return
      }
      
      this.downloadingDocId = doc.id
      
      try {
        const response = await fetch(`/api/cvs/${cvId}/${this.entryType}_entries/${this.entryId}/reference_documents/${doc.id}/download`, {
          method: 'GET',
          headers: {
            'X-CSRF-Token': this.getCSRFToken()
          }
        })

        if (response.ok) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = doc.original_filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } else {
          throw new Error('Download failed')
        }
      } catch (error) {
        console.error('Download error:', error)
        // You could emit an error event here
      } finally {
        this.downloadingDocId = null
      }
    },

    confirmDeleteDocument(document) {
      this.documentToDelete = document
      this.showDeleteModal = true
    },

    cancelDelete() {
      this.showDeleteModal = false
      this.documentToDelete = null
    },

    async deleteDocument() {
      if (!this.documentToDelete || this.deletingDocId) return
      
      const cvId = this.getCvId()
      if (!cvId) {
        console.error('Cannot delete document: No CV ID available')
        return
      }
      
      this.deletingDocId = this.documentToDelete.id
      
      try {
        const response = await fetch(`/api/cvs/${cvId}/${this.entryType}_entries/${this.entryId}/reference_documents/${this.documentToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': this.getCSRFToken(),
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          this.$emit('document-deleted', this.documentToDelete.id)
          this.showDeleteModal = false
          this.documentToDelete = null
        } else {
          throw new Error('Delete failed')
        }
      } catch (error) {
        console.error('Delete error:', error)
        // You could emit an error event here
      } finally {
        this.deletingDocId = null
      }
    },

    getCSRFToken() {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : ''
    }
  }
}
</script>

<style scoped>
.reference-list-container {
  margin-top: 1.5rem;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.list-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.title-icon {
  width: 1rem;
  height: 1rem;
  color: #6b7280;
}

.document-count {
  color: #6b7280;
  font-weight: 400;
}

.reorder-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.reorder-button:hover {
  background: #f3f4f6;
  color: #374151;
}

.reorder-button.active {
  background: #f97316;
  color: white;
  border-color: #f97316;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.empty-icon {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  opacity: 0.5;
}

.empty-text {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.empty-subtext {
  font-size: 0.75rem;
  opacity: 0.75;
}

.document-list {
  margin-bottom: 0.5rem;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.document-item:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.document-item.reorder-mode {
  cursor: move;
}

.document-item.deleting {
  opacity: 0.5;
}

.reorder-handle {
  cursor: move;
  color: #9ca3af;
  padding: 0.25rem;
}

.file-icon {
  flex-shrink: 0;
}

.file-icon svg {
  width: 1.5rem;
  height: 1.5rem;
}

.icon-pdf { color: #dc2626; }
.icon-image { color: #059669; }
.icon-document { color: #2563eb; }
.icon-file { color: #6b7280; }

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.file-separator {
  opacity: 0.5;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.download-button {
  color: #374151;
}

.download-button:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #f97316;
}

.delete-button {
  color: #6b7280;
}

.delete-button:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 28rem;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 0 1.5rem;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modal-close {
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
}

.modal-close:hover {
  color: #374151;
  background: #f3f4f6;
}

.modal-body {
  padding: 1.5rem;
  text-align: center;
}

.modal-icon {
  margin-bottom: 1rem;
}

.modal-message {
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0.5rem;
}

.modal-warning {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.btn-cancel {
  flex: 1;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-cancel:hover {
  background: #f9fafb;
}

.btn-danger {
  flex: 1;
  padding: 0.5rem 1rem;
  background: #dc2626;
  border: 1px solid #dc2626;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dark mode support */
.dark .list-title {
  color: #d1d5db;
}

.dark .title-icon,
.dark .document-count {
  color: #9ca3af;
}

.dark .reorder-button {
  background: #374151;
  border-color: #4b5563;
  color: #9ca3af;
}

.dark .reorder-button:hover {
  background: #4b5563;
  color: #d1d5db;
}

.dark .document-item {
  background: #374151;
  border-color: #4b5563;
}

.dark .document-item:hover {
  background: #4b5563;
  border-color: #6b7280;
}

.dark .file-name {
  color: #d1d5db;
}

.dark .file-meta {
  color: #9ca3af;
}

.dark .action-button {
  border-color: #4b5563;
}

.dark .download-button {
  color: #d1d5db;
}

.dark .download-button:hover:not(:disabled) {
  background: #4b5563;
  border-color: #6b7280;
}

.dark .delete-button {
  color: #9ca3af;
}

.dark .delete-button:hover:not(:disabled) {
  background: #7f1d1d;
  border-color: #dc2626;
  color: #fca5a5;
}

.dark .modal-content {
  background: #1f2937;
}

.dark .modal-title {
  color: #f9fafb;
}

.dark .modal-close {
  color: #9ca3af;
}

.dark .modal-close:hover {
  color: #d1d5db;
  background: #374151;
}

.dark .modal-message {
  color: #d1d5db;
}

.dark .modal-warning {
  color: #9ca3af;
}

.dark .btn-cancel {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}

.dark .btn-cancel:hover {
  background: #4b5563;
}
</style>